import type { Person } from '@/store/people/types';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Textarea,
  DatePicker,
  TimeInput,
} from '@/components/ui';
import { useState, useCallback } from 'react';
import { useCreateReminder } from '@/store/reminders/hooks';
import type {
  ReminderCreate,
  Reminder,
  ReminderUpdate,
} from '@/store/reminders/types';
import { useUserId } from '@/hooks/auth';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Time, CalendarDate } from '@internationalized/date';
import { useUpdateReminder, useDismissReminder } from '@/store/reminders/hooks';
import { Name } from '@/components/common/output/name';
import { getFullName } from '@/shared/lib/names';
import { useModalSize } from '@/hooks/screen';

export function ReminderModal({
  isOpen,
  onOpenChange,
  target,
  reminder,
  access = { canEdit: true, canDismiss: true },
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  target: Person | null;
  reminder?: Reminder | null;
  access?: {
    canEdit: boolean;
    canDismiss: boolean;
  };
}) {
  const modalSize = useModalSize();
  const isEdit = Boolean(reminder);
  if (!isOpen) {
    return null;
  }

  if (!isEdit && !target) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      scrollBehavior='inside'
      placement='center'
      size={modalSize}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              {isEdit ? 'Edit Reminder' : 'Add Reminder'}
            </ModalHeader>
            <ModalBody>
              <ReminderForm
                target={target}
                reminder={reminder}
                onClose={onClose}
                access={access}
                isEdit={isEdit}
              />
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

function ReminderForm({
  target,
  reminder,
  onClose,
  access,
  isEdit = false,
}: {
  target: Person | null;
  reminder?: Reminder | null;
  onClose?: () => void;
  access: {
    canEdit: boolean;
    canDismiss: boolean;
  };
  isEdit?: boolean;
}) {
  const userId = useUserId();
  const subject = reminder?.data.subject || getFullName(target?.data || {});

  const [note, setNote] = useState(reminder?.data.note || '');
  const [date, setDate] = useState<CalendarDate | null>(
    reminder?.data.due_at
      ? new CalendarDate(
          new Date(reminder.data.due_at).getFullYear(),
          new Date(reminder.data.due_at).getMonth() + 1,
          new Date(reminder.data.due_at).getDate(),
        )
      : null,
  );

  const [time, setTime] = useState<Time | null>(
    reminder?.data.due_at
      ? new Time(
          new Date(reminder.data.due_at).getHours(),
          new Date(reminder.data.due_at).getMinutes(),
        )
      : null,
  );

  const [localPosting, _setLocalPosting] = useState(false);
  const [localUpdating, _setLocalUpdating] = useState(false);
  const [localDismissing, _setLocalDismissing] = useState(false);

  const { createReminder, creating } = useCreateReminder();
  const { updateReminder, updating } = useUpdateReminder();
  const { dismissReminder, dismissing } = useDismissReminder();

  const isPosting = creating || localPosting;
  const isUpdating = updating || localUpdating;
  const isDismissing = dismissing || localDismissing;

  const handleCreateReminder = useCallback(
    async (reminderData: ReminderCreate) => {
      try {
        console.log('createReminder', reminderData);
        await createReminder(reminderData);

        return true;
      } catch (error) {
        console.error('Failed to create reminder:', error);

        return false;
      } finally {
        onClose?.();
      }
    },
    [createReminder, onClose],
  );

  const handleUpdateReminder = useCallback(
    async (reminderData: ReminderUpdate) => {
      try {
        console.log('updateReminder', reminderData);
        await updateReminder(reminderData);

        return true;
      } catch (error) {
        console.error('Failed to update reminder:', error);

        return false;
      } finally {
        onClose?.();
      }
    },
    [onClose, updateReminder],
  );

  const handleDismissReminder = useCallback(async () => {
    if (!isEdit || !reminder) {
      return false;
    }

    try {
      await dismissReminder(reminder.id);

      return true;
    } catch (error) {
      console.error('Failed to dismiss reminder:', error);

      return false;
    }
  }, [isEdit, reminder, dismissReminder]);

  const handleSave = useCallback(async () => {
    if (!note || !date || !time || !createReminder || !userId) {
      return;
    }

    const dueDate = new Date(
      date.year,
      date.month - 1,
      date.day,
      time.hour,
      time.minute,
    );
    const utcDueDate = new Date(dueDate.toISOString());

    if (isEdit && reminder) {
      const updateData: Partial<ReminderUpdate['data']> = {};

      if (note !== reminder.data.note) {
        updateData.note = note;
      }

      const originalDueAt = new Date(reminder.data.due_at);
      if (utcDueDate.getTime() !== originalDueAt.getTime()) {
        updateData.due_at = utcDueDate.toISOString();
      }

      if (userId !== reminder.data.assignee) {
        updateData.assignee = userId;
      }

      if (Object.keys(updateData).length > 0) {
        const reminderData: ReminderUpdate = {
          id: reminder.id,
          data: updateData,
        };
        console.log('reminderData UPDATING', reminderData);
        await handleUpdateReminder(reminderData);
      }
    } else {
      const reminderData: ReminderCreate = {
        id: '',
        data: {
          profile_id: target!.id,
          assignee: userId,
          note,
          due_at: utcDueDate.toISOString(),
          created_by: userId,
        },
      };

      const success = await handleCreateReminder(reminderData);
      if (success) {
        setNote('');
        setDate(null);
        setTime(null);
        onClose?.();
      }
    }
  }, [
    note,
    date,
    time,
    userId,
    target,
    reminder,
    isEdit,
    onClose,
    handleCreateReminder,
    handleUpdateReminder,
    createReminder,
  ]);

  const handleDismiss = useCallback(async () => {
    const success = await handleDismissReminder();
    if (success) {
      onClose?.();
    }
  }, [handleDismissReminder, onClose]);

  if (!userId || (!isEdit && !target)) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-row'>
        <Name
          size='sm'
          value={subject}
          className='flex-shrink-0 rounded-full bg-default-200 p-1 pr-4'
        />
        <div className='flex-grow' />
      </div>
      <Textarea
        label='Note'
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder='Enter reminder details...'
        minRows={3}
        maxRows={5}
      />
      <div className='flex flex-col gap-4 md:flex-row'>
        <DatePicker
          label='Date'
          value={date}
          onChange={setDate}
          className='flex-1'
        />
        <TimeInput
          label='Time'
          value={time}
          onChange={setTime}
          className='flex-1'
          endContent={
            <Icon icon='mdi:clock' className='text-2xl text-default-400' />
          }
        />
      </div>

      <div className='mt-4 flex flex-row justify-between'>
        {/* <div>
          <Button
            variant='light'
            color='danger'
            onPress={onClose}
            disabled={isPosting}
          >
            Cancel
          </Button>
        </div> */}

        <div className='flex w-full flex-row justify-between gap-2'>
          {isEdit && access.canDismiss && (
            <Button
              color='danger'
              onPress={handleDismiss}
              isLoading={dismissing}
            >
              Dismiss
            </Button>
          )}
          <Button
            variant='solid'
            color='primary'
            onPress={handleSave}
            isDisabled={
              !note || !date || !time || isUpdating || isPosting || isDismissing
            }
            isLoading={isUpdating || isPosting}
          >
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}
